import { Injectable } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { environment } from 'src/environments/environment'
import { I18n } from './i18n.service'

/**
 * The copy this app owns inside a device calendar event.
 *
 * A calendar event is two different things glued together: the micro-action's
 * own title and body, which are path- or Foozi-authored and already come back
 * localised from the content API, and the lines below, which are ours. Only
 * ours belong in the messages catalog — the content side is unbounded and is
 * translated server-side via the `is_translate` / `user_language` params.
 */
@Injectable({ providedIn: 'root' })
export class CalendarCopyService {
  constructor(private i18n: I18n, private title: Title) {
  }

  /**
   * The build is white-labelled — 1st90, Comet Pro, Kyper Shift, Harness
   * Intelligence and The Launch from UNC each ship their own document title.
   * Reading it here keeps one catalog key across all of them instead of a
   * near-duplicate per brand, and it is the same source `setUserAppName()`
   * already uses to tell the backend which app the user is on.
   */
  get appName(): string {
    return this.title.getTitle()
  }

  habitLabel(title: any): string {
    return `${this.i18n.t('Habit:')} ${title}`
  }

  actionLabel(title: any): string {
    return `${this.i18n.t('Action:')} ${title}`
  }

  /**
   * Notes for a micro-action event.
   *
   * `body` is the step's own text, already stripped of HTML by the caller.
   * `link` is the tool attached to the step, when there is one. `expiresOn` is
   * set only for presigned URLs, which stop working after a week — everything
   * else points at the Library instead.
   */
  microActionNotes(body: any, link?: any, expiresOn?: string): string {
    const lines = [body]

    if (link) {
      lines.push(this.toolLine(link))
      lines.push(expiresOn ? this.expiryLine(expiresOn) : this.libraryLine())
    }

    lines.push(this.completionLine())

    return lines.join('\n\n')
  }

  /** Notes for the daily/weekly learning reminder created during onboarding. */
  nextStepNotes(): string {
    return this.i18n.t(
      'Click here or open the %{appName} app to take your next step: %{link}',
      { appName: this.appName, link: environment.actionDeepLinkURL }
    )
  }

  private toolLine(link: any): string {
    return this.i18n.t(
      'Here is the tool you saw in the %{appName} app to help you complete this micro-action: %{link}',
      { appName: this.appName, link }
    )
  }

  private expiryLine(expiresOn: string): string {
    return this.i18n.t(
      'This link will expire on %{date}. To see and edit this tool any time, open your Library in the app',
      { date: expiresOn }
    )
  }

  private libraryLine(): string {
    return this.i18n.t('To see and edit this tool any time, open your Library in the app')
  }

  private completionLine(): string {
    return this.i18n.t(
      'After you have completed this micro-action, mark it complete in the %{appName} app by clicking here: %{link}',
      { appName: this.appName, link: environment.actionDeepLinkURL }
    )
  }
}
